import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, RotateCcw, Download, Share2 } from "lucide-react";

// LevelLeven-kaarten — flip-cards met een kleine oefening of inzicht.
// Perfecte brug na Vonkvinder: "trek nu een kaart".

const KAARTEN = [
  {
    k: "startpunt",
    label: "Startpunt",
    tint: "#E06B3F",
    voor: "Voor als je niet weet waar je moet beginnen.",
    oefening:
      "Zet één ding op tafel dat je gisteren hebt gedaan. Zonder oordeel. Dat is jouw startpunt.",
  },
  {
    k: "rugzak",
    label: "Rugzak",
    tint: "#F0B98D",
    voor: "Voor als het zwaar voelt.",
    oefening:
      "Schrijf drie dingen op die je meedraagt. Kies er één die vandaag even niet mee hoeft.",
  },
  {
    k: "rem",
    label: "Rem",
    tint: "#97B79D",
    voor: "Voor als het maar niet loopt.",
    oefening:
      "Vraag jezelf: wat zeg ik tegen mezelf net vóór ik stop? Verander één woord in die zin.",
  },
  {
    k: "vonk",
    label: "Vonk",
    tint: "#F0B98D",
    voor: "Voor als je iets voelde maar het ontglipte.",
    oefening:
      "Doe vandaag 20 minuten iets waarvan je gisteren dacht: 'dat zou ik weer eens moeten doen'.",
  },
  {
    k: "speelruimte",
    label: "Speelruimte",
    tint: "#78A85A",
    voor: "Voor als alles serieus is geworden.",
    oefening:
      "Probeer iets kleins op een verkeerde manier. Op je andere hand koffiezetten. Op één been tandenpoetsen. Kijk wat er dan met je gebeurt.",
  },
  {
    k: "levelmoment",
    label: "LevelMoment",
    tint: "#E06B3F",
    voor: "Voor als er iets is verschoven maar je weet niet wat.",
    oefening:
      "Bel of app iemand die je een half jaar geleden nog niet kende. Vertel wat je afgelopen maand hebt gedaan.",
  },
];

function drawCardToCanvas(kaart) {
  const canvas = document.createElement("canvas");
  const scale = 2;
  canvas.width = 720 * scale;
  canvas.height = 960 * scale;
  const ctx = canvas.getContext("2d");
  ctx.scale(scale, scale);
  // background
  ctx.fillStyle = kaart.tint;
  ctx.fillRect(0, 0, 720, 960);
  // header
  ctx.fillStyle = "#1F5132";
  ctx.font = "500 14px system-ui, sans-serif";
  ctx.fillText("LEVELLEVEN · KLEINE OEFENING", 60, 90);
  // titel
  ctx.font = "italic 82px 'Cormorant Garamond', 'Times New Roman', serif";
  ctx.fillText(kaart.label, 60, 200);
  // oefening — wrap tekst
  ctx.font = "italic 32px 'Cormorant Garamond', 'Times New Roman', serif";
  const words = ('"' + kaart.oefening + '"').split(" ");
  let line = "";
  let y = 300;
  const maxWidth = 600;
  for (const word of words) {
    const test = line + word + " ";
    if (ctx.measureText(test).width > maxWidth && line !== "") {
      ctx.fillText(line, 60, y);
      line = word + " ";
      y += 44;
    } else line = test;
  }
  ctx.fillText(line, 60, y);
  // footer
  ctx.font = "500 12px system-ui, sans-serif";
  ctx.fillText(kaart.label.toUpperCase(), 60, 900);
  ctx.textAlign = "right";
  ctx.fillText("LEVELLEVEN.NL", 660, 900);
  return canvas;
}

function downloadKaart(kaart) {
  const canvas = drawCardToCanvas(kaart);
  canvas.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `levelleven-${kaart.k}.png`;
    a.click();
    URL.revokeObjectURL(url);
  }, "image/png");
}

function shareWhatsApp(kaart) {
  const text = `LevelLeven-kaart · ${kaart.label}\n"${kaart.oefening}"\n\n→ levelleven.nl/probeer`;
  const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

function Kaart({ kaart, isOpen, onFlip, idx }) {
  return (
    <div className="relative w-full aspect-[3/4] [perspective:1000px]" data-testid={`kaart-${kaart.k}`}>
      <motion.div
        animate={{ rotateY: isOpen ? 180 : 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0 [transform-style:preserve-3d]"
      >
        {/* FRONT */}
        <button
          type="button"
          onClick={onFlip}
          className="absolute inset-0 rounded-3xl [backface-visibility:hidden] p-6 flex flex-col justify-between border-2 border-ll-ink/15 bg-ll-cream text-left hover:border-ll-ink/40 transition-colors group"
        >
          <div className="flex items-start justify-between">
            <span className="chapter-num text-3xl" style={{ color: kaart.tint }}>
              {String(idx + 1).padStart(2, "0")}
            </span>
            <motion.span
              animate={{ rotate: [0, 6, -6, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: kaart.tint }}
            >
              <Sparkles size={16} className="text-ll-ink" strokeWidth={1.6} />
            </motion.span>
          </div>
          <div>
            <p className="font-heading text-3xl sm:text-4xl italic" style={{ color: kaart.tint }}>
              {kaart.label}
            </p>
            <p className="mt-2 text-sm text-ll-navy/75 leading-relaxed">{kaart.voor}</p>
          </div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-ll-navy/50 group-hover:text-ll-coral transition-colors">
            Draai deze kaart om →
          </p>
        </button>

        {/* BACK */}
        <div
          role="button"
          tabIndex={0}
          onClick={onFlip}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onFlip(); } }}
          className="absolute inset-0 rounded-3xl [backface-visibility:hidden] [transform:rotateY(180deg)] p-6 flex flex-col justify-between text-left cursor-pointer"
          style={{ background: kaart.tint, color: "#1F5132" }}
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em]">Kleine oefening</p>
            <p className="mt-4 font-heading text-xl sm:text-2xl italic leading-snug">
              "{kaart.oefening}"
            </p>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); shareWhatsApp(kaart); }}
                data-testid={`kaart-share-${kaart.k}`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] rounded-full border border-ll-ink/30 hover:bg-ll-ink hover:text-ll-cream px-2.5 py-1 transition-colors"
                aria-label="Deel op WhatsApp"
              >
                <Share2 size={11} /> Deel
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); downloadKaart(kaart); }}
                data-testid={`kaart-download-${kaart.k}`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] rounded-full border border-ll-ink/30 hover:bg-ll-ink hover:text-ll-cream px-2.5 py-1 transition-colors"
                aria-label="Download als afbeelding"
              >
                <Download size={11} /> PNG
              </button>
            </div>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em]">
              <RotateCcw size={12} /> Terug
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function LevelKaarten() {
  const [open, setOpen] = useState({});
  return (
    <section className="py-24 sm:py-32 bg-ll-cream relative overflow-hidden" data-testid="level-kaarten">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="max-w-3xl mb-14">
          <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">LevelLeven-kaarten</p>
          <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light">
            Trek een kaart.{" "}
            <span className="italic text-ll-coral">Probeer 'm vandaag.</span>
          </h2>
          <p className="mt-6 text-ll-navy/80 leading-relaxed max-w-2xl">
            Zes kleine oefeningen uit de LevelLeven-methodiek. Kies er eentje die je aankijkt, draai 'm om, doe 'm ergens vandaag. Meer hoeft niet.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {KAARTEN.map((k, i) => (
            <Kaart
              key={k.k}
              kaart={k}
              idx={i}
              isOpen={!!open[k.k]}
              onFlip={() => setOpen((s) => ({ ...s, [k.k]: !s[k.k] }))}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
